// accountContextStore — controls which "account context" the UI is
// currently operating in.
//
// Two modes:
//   - 'personal'  — user's own data; org-scoped features (Crew,
//                   Incidents, Org Settings) are hidden. Aircraft and
//                   Flights are owner_id-scoped.
//   - 'organizational' — operating inside an active org. Aircraft and
//                   Flights queries are scoped to org_id. Crew /
//                   Incidents / Org Settings surface.
//
// A user can switch contexts at will via the top-bar switcher. The
// preference + last-chosen org persist to localStorage so the next
// app launch starts where they left off.
//
// account_kind on the user profile is the DEFAULT context for new
// sign-ins; the switcher overrides for the current session.

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { useAuthStore } from './authStore';
import { useOrgStore } from './orgStore';

export type AccountContextKind = 'personal' | 'organizational';

/** Compact representation of the active context. */
export interface AccountContext {
  kind: AccountContextKind;
  /** Active org id when kind === 'organizational'; null in personal mode. */
  orgId: string | null;
}

interface AccountContextState {
  ctx: AccountContext;
  /** Server-stored default mode for the user (from app.profiles.account_kind). */
  serverDefault: AccountContextKind;
  /** Whether the server default has been loaded yet. */
  hydrated: boolean;

  setContext: (next: AccountContext) => void;
  /** Switch to personal mode. */
  switchToPersonal: () => void;
  /** Switch to org mode with the given orgId (must be a member). */
  switchToOrganization: (orgId: string) => void;
  /** Pull the server default + last-chosen ctx from localStorage. */
  hydrate: () => Promise<void>;
  /** Persist current ctx + push the server default if it changed. */
  setServerDefault: (kind: AccountContextKind) => Promise<void>;
}

const LS_KEY = 'fyrefly:account-ctx:v1';

interface PersistedCtx {
  kind: AccountContextKind;
  orgId: string | null;
}

function readPersisted(): PersistedCtx | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<PersistedCtx>;
    if (p.kind !== 'personal' && p.kind !== 'organizational') return null;
    return { kind: p.kind, orgId: typeof p.orgId === 'string' ? p.orgId : null };
  } catch { return null; }
}

function persist(ctx: PersistedCtx) {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(LS_KEY, JSON.stringify(ctx)); } catch { /* quota */ }
}

export const useAccountContextStore = create<AccountContextState>((set, get) => ({
  ctx: { kind: 'personal', orgId: null },
  serverDefault: 'personal',
  hydrated: false,

  setContext: (next) => {
    set({ ctx: next });
    persist(next);
  },

  switchToPersonal: () => {
    const next: AccountContext = { kind: 'personal', orgId: null };
    set({ ctx: next });
    persist(next);
  },

  switchToOrganization: (orgId) => {
    const next: AccountContext = { kind: 'organizational', orgId };
    set({ ctx: next });
    persist(next);
  },

  hydrate: async () => {
    // Start with localStorage (instant) so the UI doesn't flicker
    // while we fetch the server default.
    const stored = readPersisted();
    if (stored) set({ ctx: stored });

    // Pull server default. Best-effort: any error → leave stored value alone.
    const user = useAuthStore.getState().user;
    if (supabase && user) {
      try {
        // Pull profile + orgs in parallel. Previously this only awaited
        // the profile, then peeked `useOrgStore.getState().orgs` — which
        // was empty during the sign-in race because the parallel
        // orgStore subscriber hadn't returned yet. The result: users
        // whose server account_kind = 'organizational' got dropped
        // into personal mode on every fresh sign-in.
        const [profileResult] = await Promise.all([
          supabase.from('profiles').select('account_kind').eq('id', user.id).maybeSingle(),
          // Ensure orgs are loaded before we decide context kind. The
          // orgStore caches its fetch in-flight, so calling fetchOrgs()
          // here is a no-op if the parallel subscriber already kicked
          // one off. Errors here are non-fatal — the orgs list will
          // simply be empty and we'll fall back to personal mode.
          useOrgStore.getState().fetchOrgs().catch(() => undefined),
        ]);
        const { data, error } = profileResult;
        if (error) {
          console.warn('[accountContextStore] profile read failed:', error.message);
        } else if (data) {
          const k = (data as { account_kind?: string }).account_kind === 'organizational'
            ? 'organizational' : 'personal';
          set({ serverDefault: k });
          // If no localStorage value, use the server default. If the
          // server says 'organizational' and the user has at least one
          // org, pre-select the most recently active org.
          if (!stored) {
            if (k === 'organizational') {
              const orgs = useOrgStore.getState().orgs;
              const orgId = orgs[0]?.id ?? null;
              const next: AccountContext = orgId
                ? { kind: 'organizational', orgId }
                : { kind: 'personal', orgId: null };
              set({ ctx: next });
              persist(next);
            } else {
              set({ ctx: { kind: 'personal', orgId: null } });
            }
          } else if (stored.kind === 'organizational' && !stored.orgId) {
            // Stored value says 'organizational' but no orgId attached.
            // Hydrate the orgId from the now-loaded org list (or fall
            // back to personal if the user has no orgs).
            const orgs = useOrgStore.getState().orgs;
            const orgId = orgs[0]?.id ?? null;
            const next: AccountContext = orgId
              ? { kind: 'organizational', orgId }
              : { kind: 'personal', orgId: null };
            set({ ctx: next });
            persist(next);
          }
        }
      } catch (e) {
        console.warn('[accountContextStore] hydrate() threw unexpectedly:', e);
      } finally {
        // Always mark hydrated — a transient failure must not leave the
        // store permanently stuck with hydrated: false.
        set({ hydrated: true });
      }
    } else {
      set({ hydrated: true });
    }
  },

  setServerDefault: async (kind) => {
    set({ serverDefault: kind });
    const user = useAuthStore.getState().user;
    if (!supabase || !user) return;
    const { error } = await supabase
      .from('profiles')
      .update({ account_kind: kind })
      .eq('id', user.id);
    if (error) {
      // eslint-disable-next-line no-console
      console.error('[accountContextStore] setServerDefault failed:', error.message);
    }
  },
}));

// ── Helpers used by every dual-mode store ────────────────────────────

/** True if the caller should query org-scoped rows (org_id = X) instead
 *  of (or in addition to) personal rows. */
export function isOrgActive(): boolean {
  const c = useAccountContextStore.getState().ctx;
  return c.kind === 'organizational' && !!c.orgId;
}

/** Active org id when in org mode; null otherwise. */
export function activeOrgId(): string | null {
  const c = useAccountContextStore.getState().ctx;
  return c.kind === 'organizational' ? c.orgId : null;
}

// Auto-fetch on sign-in.
useAuthStore.subscribe((state, prev) => {
  if (!prev.user && state.user) {
    void useAccountContextStore.getState().hydrate();
  }
  // On sign-out: drop to personal so the UI doesn't show stale org data.
  if (prev.user && !state.user) {
    useAccountContextStore.getState().switchToPersonal();
  }
});
